import { IconX } from '@tabler/icons-react';
import React from 'react';
import { createPortal } from 'react-dom';

import { getDrrNodes } from '@api/inventory';
import type { DrrNodesResponse } from '@api/inventory';
import { DataSheet } from '@components/ui/DataSheet';
import type { DataSheetColumn } from '@components/ui/DataSheet';
import { formatCount } from '@utils/currency';

import { STOCK_TRANSFER_LABEL, STOCK_TRANSFER_TO_WAREHOUSE_TOOLTIP } from './stockTransfer.constants';
import StockTransferHeader from './StockTransferHeader';

interface DrrNodeBreakdownModalProps {
    isOpen: boolean;
    onClose: () => void;
    channelKey: string;
    /** Absent = all-SKU node totals for the channel. */
    productId?: string;
    skuLabel?: string;
    drrWindow?: number;
    /** 'DAY' | 'MONTH' — switches the DoH column to MoH. */
    inventoryPeriod: string;
}

const formatRate = (value: number): string =>
    value >= 100 ? formatCount(Math.round(value)) : value.toFixed(1);

/**
 * Per-node DRR/DoH drill-in for a node-backed channel
 * (inventory-drr-view-v1 §3.2). The footer reconciles nodes against the
 * channel-level number; a positive remainder is unmapped-city demand — or, on
 * B2B-OR channels, the ordered-vs-sold gap, which is a fulfillment signal
 * rather than a data discrepancy.
 */
const DrrNodeBreakdownModal: React.FC<DrrNodeBreakdownModalProps> = ({
    isOpen,
    onClose,
    channelKey,
    productId,
    skuLabel,
    drrWindow,
    inventoryPeriod,
}) => {
    const [data, setData] = React.useState<DrrNodesResponse | null>(null);
    const [loading, setLoading] = React.useState(false);
    const isMonth = inventoryPeriod === 'MONTH';

    React.useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getDrrNodes({ channelKey, productId, drrWindow })
            .then(setData)
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [isOpen, channelKey, productId, drrWindow]);

    if (!isOpen) return null;

    const nodes = data?.nodes ?? [];
    // Internal drill-in: `stock` includes Stock Transfer into the warehouse, as the grid's DoH
    // does. Shown apart, like the grid: the shelf part, then the moving part.
    const hasStockTransfer = nodes.some((node) => node.stock_transfer != null);
    const columns: DataSheetColumn[] = [
        {
            key: 'name',
            header: 'Node',
            sticky: true,
            render: (_v, row) => (
                <div>
                    <div>{row.name}</div>
                    {row.city && <div className="text-xs text-slate-400">{row.city}</div>}
                </div>
            ),
        },
        {
            key: 'stock',
            header: hasStockTransfer ? 'On hand' : 'Stock',
            align: 'right',
            render: (value, row) =>
                formatCount(Math.round((value ?? 0) - (row.stock_transfer ?? 0))),
        },
        ...(hasStockTransfer
            ? [
                  {
                      key: 'stock_transfer',
                      header: STOCK_TRANSFER_LABEL,
                      renderHeader: () => (
                          <StockTransferHeader tooltip={STOCK_TRANSFER_TO_WAREHOUSE_TOOLTIP} />
                      ),
                      align: 'right' as const,
                      render: (value: number | null | undefined) =>
                          formatCount(Math.round(value ?? 0)),
                  },
              ]
            : []),
        {
            key: 'drr',
            header: 'DRR',
            align: 'right',
            render: (value) => formatRate(value ?? 0),
        },
        {
            key: 'doh',
            header: isMonth ? 'MoH' : 'DoH',
            align: 'right',
            render: (value) =>
                value == null ? 'NA' : (isMonth ? value / 30 : value).toFixed(1),
        },
    ];
    const aggregateRow = data
        ? {
              name: 'Channel total',
              stock: formatCount(
                  Math.round(
                      data.channel_stock -
                          nodes.reduce((sum, node) => sum + (node.stock_transfer ?? 0), 0),
                  ),
              ),
              ...(hasStockTransfer
                  ? {
                        stock_transfer: formatCount(
                            Math.round(
                                nodes.reduce((sum, node) => sum + (node.stock_transfer ?? 0), 0),
                            ),
                        ),
                    }
                  : {}),
              drr: formatRate(data.channel_drr),
              doh:
                  data.channel_drr > 0
                      ? ((isMonth ? 1 / 30 : 1) * (data.channel_stock / data.channel_drr)).toFixed(1)
                      : 'NA',
          }
        : undefined;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
                <div className="flex items-start justify-between border-b border-slate-100 p-6 pb-4">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">{channelKey} — per-node DRR</h3>
                        <p className="mt-1 text-sm text-slate-500">
                            {skuLabel ?? 'All SKUs'}
                            {data ? ` · ${data.effective_days}d window` : ''}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="cursor-pointer rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    >
                        <IconX className="h-5 w-5" />
                    </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-6">
                    {loading ? (
                        <div className="py-8 text-center text-sm text-slate-400">Loading...</div>
                    ) : !data || nodes.length === 0 ? (
                        <div className="py-8 text-center text-sm text-slate-400">
                            No node data for this channel yet
                        </div>
                    ) : (
                        <>
                            <DataSheet
                                data={nodes}
                                columns={columns}
                                getRowKey={(node) => node.warehouse_id}
                                aggregateRow={aggregateRow}
                                bare
                                hideHeaderBar
                            />
                            {Math.abs(data.unattributed_drr) > 0.05 && (
                                <p className="mt-3 text-xs text-slate-500">
                                    {data.unattributed_drr > 0
                                        ? `Unattributed: ${formatRate(data.unattributed_drr)}/day of channel demand is not on any node — unmapped cities, or (on outright/OR channels) orders exceeding sell-out.`
                                        : `Node sell-out exceeds the channel figure by ${formatRate(Math.abs(data.unattributed_drr))}/day (timing effects across the window boundary).`}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default DrrNodeBreakdownModal;
